import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import get from 'lodash/get'
import EditIcon from 'transfix-icons/build/EditIcon'
import { Badge } from 'transfix-ui/components/Badge'
import DataGrid, { IDataGridProps } from 'transfix-ui/components/DataGrid/DataGrid'
import { makeStyles, Typography, GridComparatorFn, GridRowParams } from 'transfix-ui/core'
import { IConnectionTypes, Scalars } from 'types/graphqlTypes'
import { useModalState } from 'utils/hooks/useModalState'
import { brandColors, IconButton, Spinner, Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { CONNECTION_CONFIG_TYPES } from '../ConnectionConfigTypes'
import { SearchForm } from '../SearchForm/SearchForm'
import { TSearchSchema } from '../SearchForm/SearchSchema'
import { useEdiInboundConnectionConfigsQuery } from './graphql/EdiInboundConnectionConfigs'
import { AddInboundConnectionConfigModal } from './modals/AddInboundConnectionConfigModal/AddInboundConnectionConfigModal'
import { TAddInboundConnectionConfigModalSchema } from './schema/AddInboundConnectionConfigModalSchema'

export const ITEMS_PER_PAGE = 20

interface IInboundConnectionConfigsTableProps {
  onEdit: (options: TAddInboundConnectionConfigModalSchema) => void
}

const useInboundConnectionConfigsTableStyles = makeStyles(theme => ({
  root: {
    background: 'transparent',
    marginTop: theme.spacing(1),
    '& thead': {
      background: 'transparent',
    },
    '& th:first-child, td:first-child': {
      paddingLeft: 0,
    },
    '& td': {
      borderBottom: `1px solid ${brandColors.coolGray3}`,
    },
  },
  dataGridWrapper: {
    padding: theme.spacing(1, 2),
    margin: theme.spacing(2, 0, 2),
    width: '80vw',
  },
  dataGrid: {
    '& .MuiDataGrid-row': {
      borderBottom: 'inset',

      '&:hover': {
        cursor: 'pointer',
      },
    },
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: theme.spacing(3, 0, 0, 2),
  },
}))

const idComparator: GridComparatorFn = (v1: any, v2: any) => {
  return v1 - v2
}

export function InboundConnectionConfigsTable({ onEdit }: IInboundConnectionConfigsTableProps) {
  const classes = useInboundConnectionConfigsTableStyles()
  const history = useHistory()
  const [currentValues, setCurrentValues] = useState<{}>({})
  const { createToggleHandler, modalState } = useModalState({
    dispatchEditModal: false,
  })

  const openEditHandler = createToggleHandler('dispatchEditModal', true)
  const closeEditHandler = createToggleHandler('dispatchEditModal', false)

  const { data, loading, refetch } = useEdiInboundConnectionConfigsQuery({
    notifyOnNetworkStatusChange: true,
  })

  const searchSubmitHandler = (searchData: TSearchSchema) => {
    const isSearch = searchData?.company?.id && searchData?.partnerType

    refetch({
      partnerApplicationId: isSearch ? searchData?.company?.id : undefined,
      partnerApplicationType: isSearch ? searchData?.partnerType : undefined,
    })
  }

  const editButtonHandler = useCallback(
    (configId: Scalars['ID']) => {
      const currentRow = data?.ediInboundConnectionConfigs?.nodes?.find(row => row?.id === configId)
      const {
        as2ConnectionConfig,
        connectionType,
        disabled,
        environment,
        filenameGlob,
        host,
        id,
        partnerApplication,
        partnerApplicationId,
        partnerApplicationType,
        pathPrefix,
        port,
        secretKey,
        username,
      } = currentRow || {}

      const company = {
        id: partnerApplicationId,
        text: get(partnerApplication, 'shipperName', get(partnerApplication, 'carrierName', '')),
        data: {
          __typename: partnerApplicationType,
        },
      }

      setCurrentValues({
        id: id || null,
        disabled: disabled === null ? true : disabled,
        environment,
        company: company || {},
        connectionType: connectionType || IConnectionTypes.FTP,
        host: host || '',
        partnerApplicationId: partnerApplicationId || null,
        filenameGlob: filenameGlob || '',
        pathPrefix: pathPrefix || '',
        secretKey: secretKey || '',
        as2ConnectionConfigId: as2ConnectionConfig?.id,
        port: port || 0,
        username: username || '',
      })

      openEditHandler()
    },
    [data?.ediInboundConnectionConfigs?.nodes, openEditHandler]
  )

  const columns: IDataGridProps['columns'] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        flex: 0.2,
        sortComparator: idComparator,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'partnerApplicationType',
        headerName: 'Partner Type',
        flex: 0.5,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'partnerApplicationId',
        headerName: 'Partner ID',
        flex: 0.4,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'partnerApplicationName',
        headerName: 'Partner Name',
        flex: 0.5,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'connectionType',
        headerName: 'Connection Type',
        flex: 0.6,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'connectionDetails',
        headerName: 'Username@Host:Port',
        flex: 0.6,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'environment',
        headerName: 'Environment',
        flex: 0.5,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'active',
        headerName: 'Active Status',
        flex: 0.5,
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ value }) => {
          const [label, tagStyle] = value
            ? ['Not Active', TAG_STYLE.error]
            : ['Active', TAG_STYLE.success]
          return (
            <Tag
              tagStyle={tagStyle}
              size={TAG_SIZE.small}
              label={label}
              style={{ width: '50%', justifyContent: 'center' }}
            />
          )
        },
      },
      {
        field: 'edit',
        headerName: 'Edit',
        flex: 0.2,
        disableClickEventBubbling: true,
        headerAlign: 'center',
        align: 'center',
        renderCell: params => {
          return (
            <IconButton
              Icon={EditIcon}
              color='primary'
              label='Edit Inbound Connection Config'
              onClick={event => {
                event.stopPropagation()
                editButtonHandler(params?.row?.id)
              }}
              size='large'
            />
          )
        },
      },
    ],
    [editButtonHandler]
  )

  const rows: IDataGridProps['rows'] = useMemo(
    () =>
      data?.ediInboundConnectionConfigs?.nodes?.map(inboundConnectionConfig => ({
        id: inboundConnectionConfig?.id,
        partnerApplicationType: inboundConnectionConfig?.partnerApplicationType,
        partnerApplicationId: inboundConnectionConfig?.partnerApplicationId,
        partnerApplicationName: get(
          inboundConnectionConfig?.partnerApplication,
          'shipperName',
          get(inboundConnectionConfig?.partnerApplication, 'carrierName')
        ),
        connectionType: inboundConnectionConfig?.connectionType,
        connectionDetails: `${inboundConnectionConfig?.username}@${inboundConnectionConfig?.host}:${inboundConnectionConfig?.port}`,
        active: inboundConnectionConfig?.disabled,
        environment: capitalize(inboundConnectionConfig?.environment),
        edit: null,
      })) ?? [],
    [data?.ediInboundConnectionConfigs?.nodes]
  )

  const rowClickHandler = (params: GridRowParams) => {
    history.push(`/connection-configs/${CONNECTION_CONFIG_TYPES.inbound}/${params.id}`)
  }

  useEffect(() => {
    refetch()
  }, [refetch])

  return (
    <>
      <SearchForm onSubmitSuccess={searchSubmitHandler} />
      <div className={classes.title}>
        <Badge
          dataTest='inbound-config-count'
          label={`${data?.ediInboundConnectionConfigs?.nodes?.length}`}
          color='gray'
          size='medium'
        />
        <Typography variant='subtitle1'>Items</Typography>
      </div>
      <div className={classes.dataGridWrapper}>
        <DataGrid
          columns={columns}
          disableColumnMenu
          headerHeight={44}
          rows={rows}
          onRowClick={params => rowClickHandler(params)}
          className={classes.dataGrid}
          autoHeight
          pagination
          sortingOrder={['asc', 'desc']}
          pageSize={ITEMS_PER_PAGE}
          loading={loading}
          componentsProps={{
            loadingOverlay: Spinner,
          }}
        />
      </div>
      {modalState.dispatchEditModal && (
        <AddInboundConnectionConfigModal
          title='Edit Inbound Connection Config'
          onClose={closeEditHandler}
          defaultValues={currentValues}
          open
          onSubmit={onEdit}
        />
      )}
    </>
  )
}
